import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import { Bouton, Etiquette, Pastille } from '../components/ui/Base'
import { MessageErreur } from '../components/ui/Etats'
import { SqueletteTableau } from '../components/ui/Squelette'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { Page, Utilisateur } from '../lib/api/types'
import { formatHorodatage, formatMontant } from '../lib/formatters'
import { useSession } from '../lib/auth'
import { libelleRole } from '../lib/roles'

/**
 * Liste des infirmiers.
 *
 * Vocabulaire **libéral** (§2.1) : honoraires, rétrocession, relevé. Jamais
 * salaire, paie ni pointeuse — un infirmier libéral n'est pas un salarié, et
 * le vocabulaire salarié, couplé à un planning imposé, est le triptyque de la
 * requalification.
 */
export function Utilisateurs() {
  const naviguer = useNavigate()
  const { estPrincipal } = useSession()
  const requete = useQuery({
    queryKey: cles.utilisateurs.liste(),
    queryFn: ({ signal }) => api.get<Page<Utilisateur>>('/utilisateurs', signal),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Infirmiers</h1>
          <p className="text-sm text-slate-600">Membres du cabinet et modes de rémunération</p>
        </div>
        {/* Seul le titulaire principal gere les comptes : proposer le bouton
            aux autres titulaires les enverrait sur un 403. */}
        {estPrincipal && (
          <Bouton onClick={() => naviguer('/utilisateurs/nouveau')}>Ajouter un infirmier</Bouton>
        )}
      </div>

      {requete.isPending ? (
        <SqueletteTableau colonnes={5} lignes={5} />
      ) : requete.isError ? (
        <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <caption className="sr-only">Infirmiers du cabinet</caption>
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-2.5">Infirmier</th>
                <th scope="col" className="px-4 py-2.5">Rôle</th>
                <th scope="col" className="px-4 py-2.5">Rémunération</th>
                <th scope="col" className="px-4 py-2.5">ADELI / RPPS</th>
                <th scope="col" className="px-4 py-2.5">Dernière connexion</th>
                <th scope="col" className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requete.data.items.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-2 font-medium text-slate-900">
                      <Pastille couleur={u.couleur} titre={u.couleur} />
                      {u.prenom} {u.nom}
                    </span>
                    <span className="text-xs text-slate-500">{u.email}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <Etiquette ton={u.role === 'TITULAIRE' ? 'info' : 'neutre'}>
                      {libelleRole(u.role)}
                    </Etiquette>
                  </td>
                  <td className="px-4 py-2.5 text-slate-700">
                    {/* Affichage conditionnel : le champ non pertinent n'est pas
                        affiché du tout, comme le schéma ne l'envoie pas. */}
                    {u.mode_remuneration === 'HORAIRE' ? (
                      <>
                        Horaire — <strong>{formatMontant(u.taux_horaire)}</strong>/h
                      </>
                    ) : (
                      <>
                        Rétrocession — <strong>{u.taux_retrocession} %</strong> au cabinet
                      </>
                    )}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-slate-600">
                    {u.numero_adeli ?? '—'} / {u.numero_rpps ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {formatHorodatage(u.derniere_connexion_at)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Link
                      to={`/utilisateurs/${u.id}/modifier`}
                      className="text-sm font-medium text-sky-700 hover:underline"
                    >
                      Modifier
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
